import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  Truck, MapPin, Clock, Package, Route, Users, 
  AlertTriangle, CheckCircle, XCircle, ArrowUpRight,
  Plus, Filter, Download, RefreshCw, Eye, Settings,
  Globe, Navigation, Calendar, TrendingUp
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ScatterChart, 
  Scatter, ComposedChart
} from 'recharts';

const Logistics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRoute, setSelectedRoute] = useState('all');
  const [timeRange, setTimeRange] = useState('7d');
  const { isDarkMode } = useChainsTheme();

  // Mock data for logistics
  const logisticsData = [
    { date: 'Mon', deliveries: 45, onTime: 42, delayed: 3, cost: 12500, efficiency: 93.3 },
    { date: 'Tue', deliveries: 52, onTime: 49, delayed: 3, cost: 13800, efficiency: 94.2 },
    { date: 'Wed', deliveries: 48, onTime: 46, delayed: 2, cost: 13200, efficiency: 95.8 },
    { date: 'Thu', deliveries: 55, onTime: 52, delayed: 3, cost: 14500, efficiency: 94.5 },
    { date: 'Fri', deliveries: 50, onTime: 48, delayed: 2, cost: 13500, efficiency: 96.0 },
    { date: 'Sat', deliveries: 38, onTime: 36, delayed: 2, cost: 10200, efficiency: 94.7 },
    { date: 'Sun', deliveries: 42, onTime: 40, delayed: 2, cost: 11500, efficiency: 95.2 },
  ];

  const routeData = [
    { id: 'RT-001', name: 'Riyadh → Jeddah', distance: 950, duration: '8h 30m', cost: 2800, efficiency: 96.2, status: 'active' },
    { id: 'RT-002', name: 'Dammam → Riyadh', distance: 420, duration: '4h 15m', cost: 1800, efficiency: 94.8, status: 'active' },
    { id: 'RT-003', name: 'Jeddah → Abha', distance: 680, duration: '6h 45m', cost: 2200, efficiency: 92.1, status: 'maintenance' },
    { id: 'RT-004', name: 'Riyadh → Tabuk', distance: 1100, duration: '10h 20m', cost: 3200, efficiency: 95.5, status: 'active' },
    { id: 'RT-005', name: 'Abha → Dammam', distance: 1200, duration: '11h 15m', cost: 3500, efficiency: 93.7, status: 'active' },
  ];

  const vehicleData = [
    { id: 'VH-001', type: 'Truck', capacity: '15 tons', driver: 'Ahmed Al-Rashid', status: 'in_transit', location: 'Riyadh → Jeddah', eta: '2h 30m' },
    { id: 'VH-002', type: 'Van', capacity: '2 tons', driver: 'Mohammed Ali', status: 'available', location: 'Riyadh Hub', eta: 'Ready' },
    { id: 'VH-003', type: 'Truck', capacity: '20 tons', driver: 'Omar Hassan', status: 'maintenance', location: 'Service Center', eta: '4h 00m' },
    { id: 'VH-004', type: 'Truck', capacity: '12 tons', driver: 'Khalid Omar', status: 'in_transit', location: 'Dammam → Riyadh', eta: '1h 45m' },
  ];

  const performanceMetrics = [
    { name: 'On-Time Delivery', value: 94.8, target: 95, trend: 'up', change: '+0.8%', icon: CheckCircle, color: 'emerald' },
    { name: 'Route Efficiency', value: 96.2, target: 94, trend: 'up', change: '+2.2%', icon: Route, color: 'blue' },
    { name: 'Cost per Delivery', value: 245, target: 250, trend: 'up', change: '-5 SAR', icon: TrendingUp, color: 'green' },
    { name: 'Vehicle Utilization', value: 87.3, target: 85, trend: 'up', change: '+2.3%', icon: Truck, color: 'orange' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'in_transit': return 'text-blue-400';
      case 'maintenance': return 'text-orange-400';
      case 'delayed': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20';
      case 'in_transit': return 'bg-blue-500/20';
      case 'maintenance': return 'bg-orange-500/20';
      case 'delayed': return 'bg-red-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const RouteCard = ({ route }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{route.name}</h3>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{route.id}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(route.status)} ${getStatusColor(route.status)}`}>
          {route.status}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Distance</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{route.distance} km</p>
        </div>
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Duration</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{route.duration}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Cost</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {route.cost}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Efficiency</span>
          <span className={`font-medium ${route.efficiency >= 95 ? 'text-green-400' : 'text-orange-400'}`}>
            {route.efficiency}%
          </span>
        </div>
      </div>

      {/* Route Visualization */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>Route Progress</span>
          <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>{route.efficiency}% Efficient</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`flex-1 h-2 rounded-full overflow-hidden ${
            isDarkMode ? 'bg-white/20' : 'bg-gray-200'
          }`}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${route.efficiency}%` }}
              className={`h-full ${route.efficiency >= 95 ? 'bg-green-500' : 'bg-orange-500'}`}
            />
          </div>
          <MapPin className={`w-4 h-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`} />
        </div>
      </div>
    </motion.div>
  );

  const VehicleCard = ({ vehicle }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{vehicle.id}</h3>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{vehicle.type} • {vehicle.capacity}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(vehicle.status)} ${getStatusColor(vehicle.status)}`}>
          {vehicle.status.replace('_', ' ')}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Driver</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{vehicle.driver}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Location</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{vehicle.location}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>ETA</p>
          <p className={`font-medium ${vehicle.status === 'available' ? 'text-green-400' : 'text-blue-400'}`}>
            {vehicle.eta}
          </p>
        </div>
      </div>

      {/* Vehicle Status Indicator */}
      <div className="mt-4 flex items-center space-x-2">
        <div className={`w-3 h-3 rounded-full ${
          vehicle.status === 'in_transit' ? 'bg-blue-500 animate-pulse' :
          vehicle.status === 'available' ? 'bg-green-500' :
          vehicle.status === 'maintenance' ? 'bg-orange-500' : 'bg-gray-500'
        }`}></div>
        <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
          {vehicle.status === 'in_transit' ? 'In Transit' :
           vehicle.status === 'available' ? 'Available' :
           vehicle.status === 'maintenance' ? 'Under Maintenance' : 'Unknown'}
        </span>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Logistics Management</h1>
          <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Route optimization and vehicle tracking</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-6 py-3 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <RefreshCw className="w-5 h-5 mr-2 inline" />
            Refresh
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            New Route
          </motion.button>
        </div>
      </motion.div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {performanceMetrics.map((metric, index) => (
          <motion.div
            key={metric.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
              isDarkMode 
                ? 'bg-white/10 border border-white/20' 
                : 'bg-white/70 border border-gray-200/50'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-${metric.color}-500/20`}>
                <metric.icon className={`w-6 h-6 text-${metric.color}-400`} />
              </div>
              <div className={`flex items-center text-sm ${
                metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
              }`}>
                <ArrowUpRight className="w-4 h-4 mr-1" />
                {metric.change}
              </div>
            </div>
            <h3 className={`text-sm mb-2 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{metric.name}</h3>
            <p className={`text-3xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.value}</p>
            <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-gray-500'}`}>Target: {metric.target}</p>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className={`flex space-x-1 p-1 backdrop-blur-sm rounded-xl border ${
        isDarkMode ? 'border-white/20' : 'border-gray-200'
      }`}>
        {['overview', 'routes', 'vehicles', 'analytics'].map((tab) => (
          <motion.button
            key={tab}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              activeTab === tab
                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </motion.button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            {/* Logistics Performance Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`backdrop-blur-sm rounded-2xl p-6 ${
                isDarkMode 
                  ? 'bg-white/10 border border-white/20' 
                  : 'bg-white/70 border border-gray-200/50'
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Weekly Performance</h3>
                <select className={`rounded-lg px-3 py-1 text-sm ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20 text-white' 
                    : 'bg-white/50 border border-gray-200 text-gray-900'
                }`}>
                  <option>Last 7 Days</option>
                  <option>Last 30 Days</option>
                  <option>Last Quarter</option>
                </select>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={logisticsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#f0f0f0"} />
                  <XAxis dataKey="date" stroke={isDarkMode ? "rgba(255,255,255,0.6)" : "#6b7280"} />
                  <YAxis stroke={isDarkMode ? "rgba(255,255,255,0.6)" : "#6b7280"} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)', 
                      border: 'none', 
                      borderRadius: '12px',
                      color: isDarkMode ? 'white' : 'black'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="deliveries" 
                    stackId="1" 
                    stroke="#3B82F6" 
                    fill="#3B82F6" 
                    fillOpacity={0.3}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="onTime" 
                    stackId="1" 
                    stroke="#10B981" 
                    fill="#10B981" 
                    fillOpacity={0.3}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="efficiency" 
                    stroke="#F59E0B" 
                    strokeWidth={3}
                    dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </motion.div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <Truck className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Active Vehicles</p>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>24</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-green-500/20 rounded-xl">
                    <Route className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Active Routes</p>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>12</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-orange-500/20 rounded-xl">
                    <Users className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Drivers</p>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>18</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {activeTab === 'routes' && (
          <motion.div
            key="routes"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Route Management</h3>
              <div className="flex items-center space-x-4">
                <select 
                  value={selectedRoute}
                  onChange={(e) => setSelectedRoute(e.target.value)}
                  className={`rounded-lg px-4 py-2 ${
                    isDarkMode 
                      ? 'bg-white/10 border border-white/20 text-white' 
                      : 'bg-white/50 border border-gray-200 text-gray-900'
                  }`}
                >
                  <option value="all">All Routes</option>
                  <option value="active">Active</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {routeData.map((route, index) => (
                <RouteCard key={route.id} route={route} />
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'vehicles' && (
          <motion.div
            key="vehicles"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Vehicle Fleet</h3>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                <Plus className="w-4 h-4 mr-2 inline" />
                Add Vehicle
              </motion.button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicleData.map((vehicle, index) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} />
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'analytics' && (
          <motion.div
            key="analytics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Cost Analysis */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <h3 className={`text-xl font-semibold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Cost Analysis</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={logisticsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#f0f0f0"} />
                    <XAxis dataKey="date" stroke={isDarkMode ? "rgba(255,255,255,0.6)" : "#6b7280"} />
                    <YAxis stroke={isDarkMode ? "rgba(255,255,255,0.6)" : "#6b7280"} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)', 
                        border: 'none', 
                        borderRadius: '12px',
                        color: isDarkMode ? 'white' : 'black'
                      }}
                    />
                    <Bar dataKey="cost" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Efficiency Distribution */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <h3 className={`text-xl font-semibold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Efficiency Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Excellent (95%+)', value: 45, color: '#10B981' },
                        { name: 'Good (90-95%)', value: 35, color: '#3B82F6' },
                        { name: 'Average (85-90%)', value: 15, color: '#F59E0B' },
                        { name: 'Below Average (<85%)', value: 5, color: '#EF4444' },
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                    >
                      {[
                        { name: 'Excellent (95%+)', value: 45, color: '#10B981' },
                        { name: 'Good (90-95%)', value: 35, color: '#3B82F6' },
                        { name: 'Average (85-90%)', value: 15, color: '#F59E0B' },
                        { name: 'Below Average (<85%)', value: 5, color: '#EF4444' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)', 
                        border: 'none', 
                        borderRadius: '12px',
                        color: isDarkMode ? 'white' : 'black'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Logistics; 